import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../../tfi/helpers/consts'

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000'

export interface ChannelConfigData {
  id: number | null
  webhook_token: string
  organization_id?: string
  organization_name?: string
  // Twilio
  twilio_enabled: boolean
  twilio_account_sid: string
  twilio_auth_token: string
  twilio_phone_number: string
  // Botmaker
  botmaker_enabled: boolean
  botmaker_api_url: string
  botmaker_api_token: string
  botmaker_default_channel_id: string
  botmaker_bot_name: string
  botmaker_phone_line_number: string
  // Email
  email_enabled: boolean
  email_host: string
  email_port: number
  email_host_user: string
  email_host_password: string
  email_use_tls: boolean
  email_from: string
  email_reply_to: string
}

const EMPTY_CONFIG: ChannelConfigData = {
  id: null,
  webhook_token: '',
  twilio_enabled: false, twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '',
  botmaker_enabled: false, botmaker_api_url: 'https://api.botmaker.com/v2.0', botmaker_api_token: '',
  botmaker_default_channel_id: '', botmaker_bot_name: '', botmaker_phone_line_number: '',
  email_enabled: false, email_host: '', email_port: 587, email_host_user: '',
  email_host_password: '', email_use_tls: true, email_from: '', email_reply_to: '',
}

export const useChannelConfig = (organizationId?: string) => {
  const { auth } = useAuth()
  const [config, setConfig] = useState<ChannelConfigData>(EMPTY_CONFIG)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const fetchOrgConfig = useCallback(async () => {
    const token = getToken()
    if (!token) return
    setLoading(true)
    try {
      const params = organizationId ? `?organization_id=${organizationId}` : ''
      const res = await axios.get(`${API_URL}/api/chat/channel-config/organization${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setConfig(res.data)
    } catch (error) {
      console.error('Error fetching channel config:', error)
    } finally {
      setLoading(false)
    }
  }, [getToken, organizationId])

  const saveOrgConfig = useCallback(async (data: Partial<ChannelConfigData>) => {
    const token = getToken()
    if (!token) return false
    setSaving(true)
    try {
      const payload = { ...data }
      if (organizationId) {
        payload.organization_id = organizationId
      }
      const res = await axios.post(
        `${API_URL}/api/chat/channel-config/organization`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      )
      setConfig(res.data)
      return true
    } catch (error: any) {
      console.error('Error saving channel config:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [getToken, organizationId])

  useEffect(() => {
    fetchOrgConfig()
  }, [fetchOrgConfig])

  return { config, setConfig, loading, saving, fetchOrgConfig, saveOrgConfig }
}
